// Centralized route resolution + cardinality control.
//
// `window.location.pathname` is reported as the `route` label on most metrics.
// Without filtering, scanner probes (/.well-known/*, /_profiler/phpinfo,
// /.env, *.php) and source-leak probes (/src/...) explode label cardinality.
//
// We use a centralized DENYLIST that catches the obvious scanner patterns —
// no per-site config required. Anything matching a deny pattern collapses to
// '(unknown)' so the dashboard stays clean by default.
//
// Sites that need stricter precision (e.g., to bucket dynamic IDs like
// '/users/:id' or to exclude every non-canonical path) can opt into ALLOWLIST
// mode by passing `knownRoutes` to `initShopeepayReporter`. When provided, any
// path not in the list (after passing the denylist) is also treated as
// '(unknown)'. Routes can include `:placeholder` segments — a real visit to
// '/users/42' will be reported as '/users/:id'.

// ---------- denylist (always applied) ----------

const DEFAULT_DENY_PATTERNS = [
  /^\/\.well-known(\/|$)/i,                  // OIDC / OAuth discovery probes
  /^\/_profiler(\/|$)/i,                      // Symfony profiler probes
  /^\/wp-(admin|login|content|json|includes)(\/|$)/i, // WordPress probes
  /^\/(phpmyadmin|cpanel|administrator|manager)(\/|$)/i, // admin-panel probes
  /^\/\.(env|git|htaccess|aws|ssh|svn|hg)(\/|$)/i,    // config / dotfile probes
  /\.(php|asp|aspx|jsp|cgi|do)(\?|$|\/)/i,            // server-side script probes (we are SPA-only)
  /^\/(src|node_modules|dist|build)\//i,              // leaked source-path probes
  /^\/\/+/,                                            // multiple-slash garbage (e.g. ///*)
  /^\/undefined(\/|$)/i,                              // JS interpolation bug accidentally hitting telemetry
];

function matchesDeny(path) {
  for (const re of DEFAULT_DENY_PATTERNS) {
    if (re.test(path)) return true;
  }
  return false;
}

// ---------- optional per-site allowlist ----------

let exactRoutes = new Set();
let patternRoutes = []; // [{ regex, label }]

function normalizePath(path) {
  if (!path) return '/';
  let p = path.split('?')[0].split('#')[0];
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function templateToRegex(template) {
  const regexStr = template
    .split('/')
    .map(seg => (seg.startsWith(':') ? '[^/]+' : escapeRegex(seg)))
    .join('/');
  return new RegExp('^' + regexStr + '$');
}

export function setKnownRoutes(routes) {
  exactRoutes = new Set();
  patternRoutes = [];
  if (!Array.isArray(routes) || routes.length === 0) return;
  for (const raw of routes) {
    if (typeof raw !== 'string') continue;
    const norm = normalizePath(raw);
    if (norm.includes('/:')) {
      patternRoutes.push({ regex: templateToRegex(norm), label: norm });
    } else {
      exactRoutes.add(norm);
    }
  }
}

export function getRoute() {
  if (typeof window === 'undefined') return '/';
  const path = normalizePath(window.location.pathname);

  // Denylist always runs first — zero-config scanner-pattern filtering.
  if (matchesDeny(path)) return '(unknown)';

  // If a site opted into allowlist mode, enforce strict matching.
  if (exactRoutes.size > 0 || patternRoutes.length > 0) {
    if (exactRoutes.has(path)) return path;
    for (const { regex, label } of patternRoutes) {
      if (regex.test(path)) return label;
    }
    return '(unknown)';
  }

  // Otherwise pass through the (denylist-cleaned) path.
  return path;
}
